import { Box, Card, Flex, H, Icon, Spinner, Text, toast } from 'dash-ui';
import styled, { useTheme } from 'styled-components';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_MONTH_FORMAT, formatCurrency } from 'dash-common';

import CurrencyConvertorInputs from './components/CurrencyConvertorInputs/CurrencyConvertorInputs';
import {
  useGetCurrencyRateData,
  useSetCurrencyRates,
} from './CurrencyConverterWidget.hooks';
import { ICurrencyInputValues } from './CurrencyConverterWidget.interfaces';
import {
  CURRENCY_INPUTS_DEFAULT_VALUE,
  CURRENCY_INPUTS_RESET_VALUE,
} from './CurrencyConverterWidget.constants';
import { getRateByCurrency } from './CurrencyConverterWidget.helpers';

const PointerWrapper = styled(Box)`
  cursor: pointer;
`;

function CurrencyConverterWidget() {
  const theme = useTheme();
  const { t } = useTranslation(['pages', 'common']);
  const { rates, loading } = useSetCurrencyRates();
  const { timestamp } = useGetCurrencyRateData();
  const [inputValues, setInputValues] = useState<ICurrencyInputValues>(
    CURRENCY_INPUTS_DEFAULT_VALUE
  );

  const isClearButtonVisible = useMemo(
    () => !isEqual(inputValues, CURRENCY_INPUTS_RESET_VALUE),
    [inputValues]
  );

  const rate = useMemo(
    () =>
      getRateByCurrency(
        rates,
        inputValues.top.currency,
        inputValues.bottom.currency
      ),
    [inputValues, rates]
  );

  return (
    <Card w="100%" p={4} pb={6} flexDirection="column" pos="relative">
      <Flex mb={5} justifyContent="space-between" alignItems="center" w="100%">
        <Text semibold lg>
          {t('pages:dashboardPage.currencyConverterWidget.title')}
        </Text>
        {isClearButtonVisible && (
          <PointerWrapper
            onClick={() => setInputValues(CURRENCY_INPUTS_RESET_VALUE)}
          >
            <Text color={theme.colors.b50}>{t('common:labels.clear')}</Text>
          </PointerWrapper>
        )}
      </Flex>
      <CurrencyConvertorInputs
        rates={rates}
        inputValues={inputValues}
        setInputValues={setInputValues}
      />
      <Text sm color={theme.colors.n70}>
        {t('pages:dashboardPage.currencyConverterWidget.label', {
          amount: formatCurrency(
            inputValues.top.price,
            inputValues.top.currency
          ),
        })}
      </Text>
      <Flex mt={1} alignItems="center" w="100%" overflow="auto">
        <H bold sm>
          {formatCurrency(
            inputValues.bottom.price,
            inputValues.bottom.currency
          )}
        </H>
        {Number(inputValues.bottom.price) > 0 && (
          <PointerWrapper mt={1}>
            <Icon
              name="contentCopy"
              size={24}
              color={theme.colors.b50}
              ml={3}
              onClick={() => {
                navigator.clipboard.writeText(
                  formatCurrency(
                    inputValues.bottom.price,
                    inputValues.bottom.currency
                  )
                );
                toast.success(
                  t('pages:dashboardPage.currencyConverterWidget.copied')
                );
              }}
            />
          </PointerWrapper>
        )}
      </Flex>
      <Flex alignItems="center">
        <Text sm color={theme.colors.n70}>
          {t('pages:dashboardPage.currencyConverterWidget.for', {
            date: moment(timestamp).format(DATE_MONTH_FORMAT),
          })}
        </Text>
        <Box px={1}>
          <Text>&middot;</Text>
        </Box>
        <Text sm color={theme.colors.n70}>
          {t('pages:dashboardPage.currencyConverterWidget.rate', {
            from: formatCurrency(
              inputValues.top.price ? 1 : 0,
              inputValues.top.currency
            ),
            to: formatCurrency(rate, inputValues.bottom.currency),
          })}
        </Text>
      </Flex>
      {loading && <Spinner fullContent />}
    </Card>
  );
}

export default CurrencyConverterWidget;
